import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';

import { useFeatureFlagSplit } from '@verantos-dev/core-utils';

import { useAnnotationToolAllowedApps } from './useAnnotationToolAllowedApps';
import { useAnnotationToolAppId } from './useAnnotationToolAppId';
import { useAnnotationToolSidebarConfig } from './useAnnotationToolSidebarConfig';
import { useAuth } from '../core/auth';
import { UserRolesIdsEnum } from '../core/enums';
import {
  dashboardApiSlice,
  useGetHasAnnotationTasksQuery,
  useGetHasSupervisionBatchesQuery,
} from '../features/dashboard/api/dashboardApiSlice';
import { ANNOTATION_DASHBOARD_TAG } from '../features/dashboard/constants';
import { AppPageLayoutContainer } from '../layout';
// import { AppPageLayoutContainer } from '@verantos-dev/shared-layout';
import { ANNOTATION_TOOL_ROLE_MAPPING, AnnotationToolFeatureId } from '../lib';
import { useAppDispatch } from '../lib/store';

/**
 * Internal Platform-specific container for AppPageLayout.
 * Configures layout behavior and sidebar configuration for the internal platform app.
 * Currently, no chat interfaces or special padding requirements are needed.
 */
export const AnnotationToolAppPageLayoutContainer = () => {
  const { user } = useAuth();
  const userRoleIds = user.roles.map((role) => role.roleId);
  const adminRequiredRoles = ANNOTATION_TOOL_ROLE_MAPPING[AnnotationToolFeatureId.adminProjects];
  const isAdmin = Array.isArray(adminRequiredRoles)
    ? adminRequiredRoles.some((role) => userRoleIds.includes(role))
    : userRoleIds.includes(adminRequiredRoles);
  const isUser = userRoleIds.includes(UserRolesIdsEnum.ANNOTATION_USER);

  const sidebarConfig = useAnnotationToolSidebarConfig();
  const appId = useAnnotationToolAppId();
  const allowedApps = useAnnotationToolAllowedApps();
  const { data: hasSupervisionBatchesData } = useGetHasSupervisionBatchesQuery(undefined, {
    skip: !isUser && !isAdmin,
  });
  const { data: hasAnnotationTasksData } = useGetHasAnnotationTasksQuery(undefined, {
    skip: !isUser && !isAdmin,
  });

  const location = useLocation();
  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(dashboardApiSlice.util.invalidateTags([ANNOTATION_DASHBOARD_TAG]));
  }, [location.pathname]);

  // Check if user has both supervision batches and annotation tasks (for nested features)
  const hasBothSupervisionAndAnnotation =
    hasSupervisionBatchesData?.data?.has === true && hasAnnotationTasksData?.data?.has === true;

  // Check if user has only annotation tasks (no supervision batches) - for nested features under Annotator Workspace
  const hasOnlyAnnotationTasks =
    hasSupervisionBatchesData?.data?.has === false && hasAnnotationTasksData?.data?.has === true;

  // Filter and transform allowed apps based on API conditions
  let filteredAllowedApps = allowedApps
    .map((app) => {
      // Handle annotation-dashboard: conditionally rename and add nested features
      if (app.appId === AnnotationToolFeatureId.annotationDashboard) {
        // If API data is not loaded yet, hide this option
        if (hasSupervisionBatchesData?.data === undefined) {
          return null;
        }

        const hasSupervision = hasSupervisionBatchesData.data.has === true;
        const hasAnnotation = hasAnnotationTasksData?.data?.has === true;

        // ANNOTATION_MANAGER + ANNOTATION_USER retains Supervisor Workspace access whenever
        // there are no active supervision batches, regardless of annotation tasks — Review
        // Conflicts is shown read-only, and My Assignments count may be 0.
        const isManagerWithoutSupervision = isAdmin && isUser && !hasSupervision;

        // My Projects (Supervisor Projects) is shown alongside actual supervision batches, or
        // for a manager without supervision batches who does have annotation tasks — but not
        // for a manager with no active work at all.
        const showSupervisorProjects =
          hasSupervision || (isManagerWithoutSupervision && hasAnnotation);

        // Rename to "Supervisor Workspace" when the user actually has supervision batches,
        // for an admin-only user (no ANNOTATION_USER role) who has no Annotator Workspace to
        // fall back to, or for the manager-without-supervision case above.
        if (hasSupervision || (isAdmin && !isUser) || isManagerWithoutSupervision) {
          // Build nested features array
          const nestedFeatures = [];

          // Add supervisor projects (mutually exclusive with annotatorProjects)
          if (showSupervisorProjects) {
            nestedFeatures.push({
              featureId: AnnotationToolFeatureId.supervisorProjects,
            });
          }

          // Always add Review Conflicts in the Supervisor Workspace
          nestedFeatures.push({
            featureId: AnnotationToolFeatureId.annotationSupervisor, // Review Conflicts
          });

          // Add My Assignments alongside actual supervision batches, or retains manager access
          // (count may be 0)
          if (hasSupervision || isManagerWithoutSupervision) {
            nestedFeatures.push({
              featureId: AnnotationToolFeatureId.annotation, // My Assignments (annotator-queue)
            });
          }

          return {
            ...app,
            appName: 'annotation-supervisor', // This will use 'sidebar.annotation-supervisor' locale key
            features: nestedFeatures,
          };
        }

        // An ANNOTATION_USER without ANNOTATION_MANAGER and without supervision batches gets
        // "Annotator Workspace" (counts may be 0, driven by the backend). ANNOTATION_MANAGER
        // users without supervision batches are already handled above.
        if (isUser && !hasSupervision) {
          return {
            ...app,
            appName: 'annotation', // This will use 'sidebar.annotation' locale key
            features: [
              {
                featureId: AnnotationToolFeatureId.annotatorProjects, // My Projects (mutually exclusive with supervisorProjects)
              },
              {
                featureId: AnnotationToolFeatureId.annotation, // My Assignments (annotator-queue)
              },
            ],
          };
        }

        return null;
      }

      // Handle metrics: add nested features when user has supervision batches AND has metrics role, otherwise hide
      if (app.appId === AnnotationToolFeatureId.metrics) {
        const metricsRequiredRoles = ANNOTATION_TOOL_ROLE_MAPPING[AnnotationToolFeatureId.metrics];
        const hasMetricsRole = Array.isArray(metricsRequiredRoles)
          ? metricsRequiredRoles.some((role) => userRoleIds.includes(role))
          : userRoleIds.includes(metricsRequiredRoles);
        if (hasSupervisionBatchesData?.data?.has === true && hasMetricsRole) {
          return {
            ...app,
            features: [
              { featureId: AnnotationToolFeatureId.metricsDashboard },
              { featureId: AnnotationToolFeatureId.metricsSummary },
              { featureId: AnnotationToolFeatureId.metricsEncounterStats },
              { featureId: AnnotationToolFeatureId.metricsIRRScore },
              { featureId: AnnotationToolFeatureId.metricsHolidays },
            ],
          };
        }
        return null;
      }

      return app;
    })
    .filter((app) => {
      // Filter out null entries (hidden items)
      if (app === null) {
        return false;
      }

      // If user has supervision batches, hide standalone annotation-supervisor
      // (it will be shown as nested feature under Supervisor Workspace)
      if (hasSupervisionBatchesData?.data?.has === true) {
        // Hide standalone annotation-supervisor when it's nested under dashboard
        if (app.appId === AnnotationToolFeatureId.annotationSupervisor) {
          return false;
        }
      }

      // If user has both supervision and annotation tasks, hide standalone annotation app
      // (it will be shown as nested feature under Supervisor Workspace)
      if (hasBothSupervisionAndAnnotation) {
        // Hide standalone annotation/annotator-queue when it's nested under dashboard
        if (app.appId === AnnotationToolFeatureId.annotation) {
          return false;
        }
      }

      // If user has only annotation tasks (no supervision batches), hide standalone annotation app
      // (it will be shown as nested feature under Annotator Workspace)
      if (hasOnlyAnnotationTasks) {
        // Hide standalone annotation/annotator-queue when it's nested under dashboard
        if (app.appId === AnnotationToolFeatureId.annotation) {
          return false;
        }
      }

      // If user is admin, hide standalone Manage Projects app
      // (it will be shown as nested feature under Administration Workspace)
      if (isAdmin && app.appId === AnnotationToolFeatureId.adminProjects) {
        return false;
      }

      // If user is not admin, hide Manage Projects app completely
      if (!isAdmin && app.appId === AnnotationToolFeatureId.adminProjects) {
        return false;
      }

      // Existing filtering logic for other apps
      if (
        !hasSupervisionBatchesData?.data?.has &&
        app.appId === AnnotationToolFeatureId.annotationSupervisor
      ) {
        return false;
      }

      if (!hasAnnotationTasksData?.data?.has && app.appId === AnnotationToolFeatureId.annotation) {
        return false;
      }

      return true;
    }) as typeof allowedApps;

  // Add Administration Workspace for admin users
  if (isAdmin) {
    filteredAllowedApps = [
      ...filteredAllowedApps,
      {
        appId: 'administration-workspace',
        appName: 'administration-workspace',
        features: [
          {
            featureId: AnnotationToolFeatureId.adminProjects, // Manage Projects
          },
        ],
        sectionTitleId: 'annotation-tool',
      },
    ];
  }

  const { formatMessage } = useIntl();
  // TODO: cleanup with cp_app_header_rename ff
  const isCurationPlatformHeaderEnabled = useFeatureFlagSplit('cp_app_header_rename', false);

  // Commented below is the logic to create header name based on user role
  // const getWorkspaceName = (): string => {
  //   if (isAdmin) {
  //     return formatMessage({ id: 'sidebar.administration-workspace' });
  //   }
  //   if (hasSupervisionBatchesData?.data?.has === true) {
  //     return formatMessage({ id: 'sidebar.annotation-supervisor' });
  //   }
  //   if (hasAnnotationTasksData?.data?.has === true) {
  //     return formatMessage({ id: 'sidebar.annotation' });
  //   }
  //   // Fallback to default
  //   return formatMessage({ id: 'sidebar.annotation-dashboard' });
  // };

  return (
    <AppPageLayoutContainer
      sidebarConfig={sidebarConfig}
      allowedApps={filteredAllowedApps}
      appId={appId}
      // TODO: cleanup with cp_app_header_rename ff — once rolled out, drop the ternary and
      // the legacy 'appHeader.platform.name' branch; keep 'appHeader.platform.name.curation' only.
      platformNameMessageId={
        isCurationPlatformHeaderEnabled
          ? 'appHeader.platform.name.curation'
          : 'appHeader.platform.name'
      }
      // TODO: cleanup with cp_app_header_rename ff — drop the ternary and the legacy
      // formatMessage('appHeader.applicationName') section label; keep the empty-string
      // (no section) branch. Also remove the now-unused 'appHeader.applicationName' locale
      // key from apps/annotation-tool/src/core/localization/lib/en/appLayoutMessagesEn.ts
      // once nothing else consumes it.
      getSectionName={() =>
        isCurationPlatformHeaderEnabled ? '' : formatMessage({ id: 'appHeader.applicationName' })
      }
      // TODO: cleanup with cp_app_header_rename ff — once rolled out, drop the flag and
      // always hide the sidebar section title (or remove sectionTitleId from
      // ANNOTATION_TOOL_ALLOWED_APPS and the dynamic administration-workspace entry above,
      // since it would then be dead data).
      hideSectionTitles={Boolean(isCurationPlatformHeaderEnabled)}
    />
  );
};
